import { useQueryCache } from "@pinia/colada";
import { ORDER, SORT } from "~/constants";
import { TAGS_QUERY_KEY } from "~/queries/tags";
import AuthAPI from "~/services/auth";
import SubscriptionAPI from "~/services/subscription";

const useUserStore = defineStore("user", {
  state: () => ({
    user: null as null | IModelUser,
    devices: [] as IModelDevice[],
    followed: {} as Record<IModelFollowedItem["id"], IModelFollowedItem>,
    selected: {} as Record<string, boolean>,
    token: null as null | string,
    userTokenData: null as null | IModelUserTokenData,
    creditCard: null as null | IModelCreditCard,
    creditCardToken: null as null | string,
    ppvTickets: [] as IModelCommerce[],
    pendingAccessTokenRefresh: null as null | Promise<void>,
    hasSubscription: false,
  }),
  persist: {
    storage: localStorage,
    pick: ["user", "token", "userTokenData", "followed", "devices"],
  },
  getters: {
    isAuthenticated: state => Boolean(state.userTokenData?.token && state.user),

    followedIdentitiesIds: (state) => {
      return Object.values(state.followed).reduce((acc: string[], item) => {
        if (item.identitiesId && item.identitiesId.length > 0) {
          item.identitiesId.forEach((id) => {
            if (!acc.includes(id))
              acc.push(id);
          });
        }
        return acc;
      }, []);
    },

    followedItems: (state) => {
      return Object.values(state.followed);
    },
  },

  actions: {
    logout(options: { revokeToken: boolean } = { revokeToken: true }): void {
      const segment = useSegment();
      const queryClient = useQueryCache();

      if (options.revokeToken)
        this.userTokenData?.token && AuthAPI.logout(this.userTokenData.token);

      // Clear user data from store
      this.token = null;
      this.userTokenData = null;
      this.user = null;
      this.creditCard = null;
      this.creditCardToken = null;
      this.selected = {};
      this.followed = {};
      this.devices = [];
      this.ppvTickets = [];
      this.hasSubscription = false;

      queryClient.invalidateQueries({
        key: TAGS_QUERY_KEY.root,
      });

      segment.reset();
    },
    refreshAccessToken(): Promise<void> {
      return AuthAPI.refreshAccessToken();
    },
    async fetchSubscriptions(): Promise<boolean> {
      const appConfig = useRuntimeConfig().public;
      const isPaymentEnabled = appConfig.APP_CUSTOMER.enable_payment || appConfig.APP_FLAGS.hasPayments;

      // Only fetch if payment is enabled and user is authenticated
      if (!isPaymentEnabled || !this.isAuthenticated) {
        this.hasSubscription = false;
        return false;
      }

      try {
        const response = await SubscriptionAPI.loadUserSubscriptions(
          {
            page: 0,
            pageSize: 5,
            sort: SORT.added,
            order: ORDER.desc,
            count: false,
          },
          true, // active: true means get active subscriptions only (canceled: false is handled by the API)
        );

        const hasSubscription = (response.content.entries?.length ?? 0) > 0 || (response.content.entryCount ?? 0) > 0;
        this.hasSubscription = hasSubscription;
        return hasSubscription;
      }
      catch (error) {
        console.warn("Failed to fetch user subscriptions:", error);
        this.hasSubscription = false;
        return false;
      }
    },
  },
});

export default useUserStore;

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
